import React from 'react';
import { ChevronLeft, ExternalLink } from 'lucide-react';
import './MiDetailLayout.css';

export function MiDetailField({ label, value, children, wide }) {
  return (
    <div className={`mi-detail-field${wide ? ' mi-detail-field--wide' : ''}`}>
      <div className="mi-detail-field__label">{label}</div>
      <div className="mi-detail-field__value">{children || value || '—'}</div>
    </div>
  );
}

export function MiDetailSection({ title, children, className = '' }) {
  return (
    <section className={`mi-detail-section report-card ${className}`.trim()}>
      {title ? <h2 className="mi-detail-section__title report-card-title">{title}</h2> : null}
      <div className="mi-detail-section__body">{children}</div>
    </section>
  );
}

export function MiDetailStatusPill({ active, label }) {
  const text = label || (active ? 'Active' : 'Inactive');
  const modifier = active ? 'mi-detail-status-pill--active' : 'mi-detail-status-pill--inactive';
  return <span className={`mi-detail-status-pill pill ${modifier}`}>{text}</span>;
}

export function MiDetailPlatformChip({ platform }) {
  const key = platform === 'meta' ? 'meta' : platform === 'google' ? 'google' : 'linkedin';
  const label = platform === 'meta' ? 'Meta' : platform === 'google' ? 'Google' : 'LinkedIn';
  return <span className={`mi-detail-platform-chip mi-detail-platform-chip--${key}`}>{label}</span>;
}

export function MiDetailExternalButton({ href, children }) {
  if (!href) return null;
  return (
    <a
      className="btn btn-sm btn-primary mi-detail-action-btn"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      <ExternalLink size={15} strokeWidth={2} />
      {children}
    </a>
  );
}

export function MiDetailLinkCard({ href, label, text }) {
  if (!href) return null;
  const display = text || href;
  return (
    <div className="mi-detail-link-card">
      {label ? <div className="mi-detail-link-card__label">{label}</div> : null}
      <a
        className="mi-detail-link-card__url"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={display}
      >
        {display}
      </a>
      <a
        className="mi-detail-link-card__open btn btn-sm"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
      >
        <ExternalLink size={14} />
        Open link
      </a>
    </div>
  );
}

export function MiDetailLoading({ message = 'Loading…' }) {
  return (
    <div className="filing-details-page mi-detail-page">
      <div className="content mi-detail-content mi-detail-content--state">
        <div className="mi-detail-state report-card">
          <p className="mi-detail-state__text">{message}</p>
        </div>
      </div>
    </div>
  );
}

export function MiDetailError({ message, backLabel, onBack }) {
  return (
    <div className="filing-details-page mi-detail-page">
      <div className="topbar mi-detail-topbar">
        <div className="topbar-left">
          {onBack ? (
            <button type="button" className="link-back" onClick={onBack}>
              <ChevronLeft size={14} />
              {backLabel}
            </button>
          ) : null}
        </div>
      </div>
      <div className="content mi-detail-content mi-detail-content--state">
        <div className="mi-detail-state report-card">
          <p className="mi-detail-state__text">{message}</p>
          {onBack ? (
            <button type="button" className="btn btn-sm" onClick={onBack}>
              {backLabel}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function MiDetailLayout({
  backLabel,
  onBack,
  title,
  titleRef,
  subtitle,
  meta,
  badge,
  actions,
  kpis,
  children,
}) {
  return (
    <div className="filing-details-page mi-detail-page">
      <div className="topbar mi-detail-topbar">
        <div className="topbar-left mi-detail-topbar__left">
          <button type="button" className="link-back" onClick={onBack}>
            <ChevronLeft size={14} />
            {backLabel}
          </button>
          <div className="mi-detail-hero">
            {(meta || badge) ? (
              <div className="mi-detail-hero__meta">
                {meta}
                {badge}
              </div>
            ) : null}
            <h1 className="mi-detail-hero__title">{title}</h1>
            {subtitle ? <p className="mi-detail-hero__subtitle">{subtitle}</p> : null}
            {titleRef ? (
              <p className="mi-detail-hero__ref">
                <span className="mi-detail-hero__ref-label">ID</span>
                <span className="mi-detail-hero__ref-value">{titleRef}</span>
              </p>
            ) : null}
          </div>
        </div>
        {actions ? <div className="topbar-right mi-detail-topbar__actions">{actions}</div> : null}
      </div>

      <div className="content mi-detail-content">
        {kpis ? <div className="kpi-banner-grid mi-detail-kpis">{kpis}</div> : null}
        {children}
      </div>
    </div>
  );
}
